import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Loader2, ArrowRight, Facebook, Globe, FileText, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { login, register, API_URL } from '../../api/api';
import { showToast } from '../../Layout';
import ReCAPTCHA from 'react-google-recaptcha';

export default function AuthModal({ isOpen, onClose, onLoginSuccess }) {
  const { t } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const executeRegistration = async () => {
    if (!captchaToken) {
        showToast(t('pleaseCompleteCaptcha', 'Please complete the CAPTCHA'), 'error');
        return;
    }
    setIsLoading(true);
    try {
      const response = await register({ ...formData, captchaToken });
      localStorage.setItem('swapx_user', JSON.stringify(response));
      showToast(t('registerSuccess'), 'success');
      if (onLoginSuccess) onLoginSuccess(response);
      onClose();
    } catch (error) {
      const errorMsg = error.message === 'Invalid email or password' ? t('invalidEmailPassword') : (t(error.message) || t('authFailed'));
      showToast(errorMsg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLogin) {
      setIsLoading(true);
      try {
        const response = await login(formData.email, formData.password);
        localStorage.setItem('swapx_user', JSON.stringify(response));
        showToast(t('loginSuccess'), 'success');
        if (onLoginSuccess) onLoginSuccess(response);
        onClose();
      } catch (error) {
        const errorMsg = error.message === 'Invalid email or password' ? t('invalidEmailPassword') : (t(error.message) || t('authFailed'));
        showToast(errorMsg, 'error');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Show terms instead of registering immediately
      setShowTerms(true);
    }
  };

  const handleGoogleLogin = () => {
      window.location.href = `${API_URL}/auth/google`;
  };

  const handleFacebookLogin = () => {
      window.location.href = `${API_URL}/auth/facebook`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative bg-background w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden border border-border flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-border flex justify-between items-center bg-muted/30">
                <h2 className="text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
                    {showTerms ? <FileText className="text-primary" /> : null}
                    {showTerms ? t('termsAndConditions', 'Terms & Rules') : (isLogin ? t('signIn') : t('register'))}
                </h2>
                <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
                    <X size={24} />
                </button>
            </div>

            <div className="p-8">
                {showTerms ? (
                    <motion.div 
                        initial={{ opacity: 0, x: 20 }} 
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-6"
                    >
                        <div className="bg-muted/50 p-4 rounded-2xl h-48 overflow-y-auto border border-border text-sm text-muted-foreground leading-relaxed space-y-3">
                            <p className="font-bold text-foreground">Welcome to Ahlafot!</p>
                            <p>1. <strong className="text-foreground">Respect the Community:</strong> Treat all traders with respect. Harassment or abusive behavior will lead to an immediate ban.</p>
                            <p>2. <strong className="text-foreground">Accurate Listings:</strong> Ensure all items you upload are described accurately. Do not misrepresent the condition of your items.</p>
                            <p>3. <strong className="text-foreground">No Illegal Items:</strong> The trading of illegal, dangerous, or strictly prohibited items is not allowed on the platform.</p>
                            <p>4. <strong className="text-foreground">Liability:</strong> Ahlafot provides the platform but is not liable for the outcome of any physical trades. Trade safely and responsibly.</p>
                            <p>By clicking "I Agree", you confirm that you have read and will abide by these rules.</p>
                        </div>
                        
                        <div className="flex justify-center mt-4">
                            <ReCAPTCHA
                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                                onChange={(token) => setCaptchaToken(token)}
                                theme="light"
                            />
                        </div>

                        <div className="flex gap-4 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowTerms(false)}
                                className="flex-1 py-4 rounded-2xl font-black uppercase tracking-widest text-sm bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                            >
                                {t('decline', 'Decline')}
                            </button>
                            <button
                                type="button"
                                onClick={executeRegistration}
                                disabled={isLoading}
                                className="flex-1 bg-primary text-primary-foreground py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                            >
                                {isLoading ? <Loader2 className="animate-spin" /> : (
                                    <>
                                        <CheckCircle2 size={18} />
                                        {t('iAgree', 'I Agree')}
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div 
                        initial={{ opacity: 0, x: -20 }} 
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {!isLogin && (
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">
                                        {t('fullName')}
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                        <input
                                            type="text"
                                            name="full_name"
                                            required
                                            value={formData.full_name}
                                            onChange={handleChange}
                                            className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 transition-all font-bold"
                                            placeholder={t('fullNamePlaceholder')}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">
                                    {t('email')}
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 transition-all font-bold"
                                        placeholder={t('emailAddressPlaceholder')}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">
                                    {t('password')}
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                    <input
                                        type="password"
                                        name="password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 transition-all font-bold"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-primary text-primary-foreground py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 mt-6"
                            >
                                {isLoading ? <Loader2 className="animate-spin" /> : (
                                    <>
                                        {isLogin ? t('signIn') : t('continue', 'Continue')}
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Social Logins */}
                        <div className="mt-6">
                            <div className="relative mb-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-border"></div>
                                </div>
                                <div className="relative flex justify-center text-xs uppercase font-black tracking-widest">
                                    <span className="bg-background px-4 text-muted-foreground">{t('orContinueWith')}</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <button 
                                    onClick={handleGoogleLogin}
                                    className="flex items-center justify-center gap-2 py-4 bg-muted/50 rounded-2xl hover:bg-muted transition-all font-black text-xs uppercase tracking-widest active:scale-95"
                                >
                                    <Globe size={18} className="text-primary" />
                                    {t('google')}
                                </button>
                                <button 
                                    onClick={handleFacebookLogin}
                                    className="flex items-center justify-center gap-2 py-4 bg-muted/50 rounded-2xl hover:bg-muted transition-all font-black text-xs uppercase tracking-widest active:scale-95"
                                >
                                    <Facebook size={18} className="text-[#1877F2]" />
                                    {t('facebook')}
                                </button>
                            </div>
                        </div>

                        <div className="mt-8 text-center">
                            <p className="text-sm text-muted-foreground font-bold">
                                {isLogin ? t('noAccount') : t('haveAccount')}
                            </p>
                            <button
                                onClick={() => { setIsLogin(!isLogin); setShowTerms(false); }}
                                className="mt-2 text-primary font-black uppercase tracking-widest text-xs hover:underline"
                            >
                                {isLogin ? t('register') : t('signIn')}
                            </button>
                        </div>
                    </motion.div>
                )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
